import { useMemo, useState } from 'react'
import { Button, ConfigProvider, Flex, Switch, Typography, theme } from 'antd'
import { useDarkMode } from '../hooks/useDarkMode.tsx'
import { useCookieConsent } from '../hooks/useCookieConsent.tsx'
import { BRAND, getAntdTheme } from '../theme/antdTheme.ts'

const { Text, Title, Link } = Typography
const { useToken } = theme

const PRIVACY_URL = 'https://bitquery.io/privacy-policy'

const CookieGlyph = () => (
    <svg viewBox="0 0 32 32" width="26" height="26" aria-hidden focusable="false">
        <circle cx="16" cy="16" r="13" fill="currentColor" opacity="0.14" />
        <circle cx="16" cy="16" r="13" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.45" />
        <circle cx="12" cy="11.5" r="1.7" fill="currentColor" />
        <circle cx="20.5" cy="13" r="1.4" fill="currentColor" />
        <circle cx="13.5" cy="19" r="1.5" fill="currentColor" />
        <circle cx="20.5" cy="20" r="1.3" fill="currentColor" />
        <circle cx="16.5" cy="15.5" r="1" fill="currentColor" />
    </svg>
)

const CategoryRow = ({
    title,
    description,
    checked,
    disabled,
    onChange,
}: {
    title: string
    description: string
    checked: boolean
    disabled?: boolean
    onChange?: (checked: boolean) => void
}) => (
    <Flex justify="space-between" align="flex-start" gap={16}>
        <Flex vertical gap={2} style={{ minWidth: 0 }}>
            <Text strong style={{ fontSize: 13 }}>
                {title}
            </Text>
            <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.4 }}>
                {description}
            </Text>
        </Flex>
        <Switch checked={checked} disabled={disabled} onChange={onChange} />
    </Flex>
)

const CookieConsentCard = () => {
    const { token } = useToken()
    const { acceptAll, rejectAll, save } = useCookieConsent()
    const [managing, setManaging] = useState(false)
    const [analytics, setAnalytics] = useState(true)

    const accent = token.colorBgBase === '#06090d' ? BRAND.magentaPink : BRAND.magenta

    return (
        <>
            <style>{`@keyframes bq-cookie-in{from{opacity:0;transform:translate(-50%,16px)}to{opacity:1;transform:translate(-50%,0)}}`}</style>
            <div
                role="dialog"
                aria-label="Cookie preferences"
                aria-modal="false"
                style={{
                    position: 'fixed',
                    bottom: 16,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 2000,
                    width: 'min(680px, calc(100vw - 24px))',
                    background: token.colorBgElevated,
                    color: token.colorText,
                    border: `1px solid ${token.colorBorderSecondary}`,
                    borderRadius: token.borderRadiusLG ?? 8,
                    boxShadow: '0 8px 28px rgba(0, 0, 0, 0.22)',
                    padding: 20,
                    borderTop: `3px solid ${accent}`,
                    animation: 'bq-cookie-in 0.28s cubic-bezier(0.16, 1, 0.3, 1)',
                }}
            >
                <Flex gap={14} align="flex-start">
                    <span style={{ color: accent, flex: '0 0 auto', marginTop: 1 }}>
                        <CookieGlyph />
                    </span>

                    <Flex vertical gap={14} style={{ flex: 1, minWidth: 0 }}>
                        <Flex vertical gap={4}>
                            <Title level={5} style={{ margin: 0 }}>
                                We value your privacy
                            </Title>
                            <Text type="secondary" style={{ fontSize: 13, lineHeight: 1.5 }}>
                                We use strictly necessary cookies to keep you signed in, and optional
                                analytics cookies to understand how Moneyflow is used. You're in control —
                                read more in our{' '}
                                <Link href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
                                    Privacy Policy
                                </Link>
                                .
                            </Text>
                        </Flex>

                        {managing && (
                            <Flex
                                vertical
                                gap={14}
                                style={{
                                    padding: '14px 16px',
                                    background: token.colorFillQuaternary,
                                    borderRadius: token.borderRadius,
                                }}
                            >
                                <CategoryRow
                                    title="Strictly necessary"
                                    description="Required for sign-in, security and core features. Always on."
                                    checked
                                    disabled
                                />
                                <CategoryRow
                                    title="Analytics"
                                    description="Google Analytics & Microsoft Clarity — anonymous usage stats that help us improve."
                                    checked={analytics}
                                    onChange={setAnalytics}
                                />
                            </Flex>
                        )}

                        <Flex gap={8} wrap="wrap" justify="flex-end" align="center">
                            {!managing ? (
                                <>
                                    <Button type="text" onClick={() => setManaging(true)}>
                                        Manage
                                    </Button>
                                    <Button onClick={rejectAll}>Reject all</Button>
                                    <Button type="primary" onClick={acceptAll}>
                                        Accept all
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Button type="text" onClick={() => setManaging(false)}>
                                        Back
                                    </Button>
                                    <Button onClick={rejectAll}>Reject all</Button>
                                    <Button type="primary" onClick={() => save(analytics)}>
                                        Save choices
                                    </Button>
                                </>
                            )}
                        </Flex>
                    </Flex>
                </Flex>
            </div>
        </>
    )
}

export const CookieConsent = () => {
    const { darkModeEnabled } = useDarkMode()
    const { decided } = useCookieConsent()

    const themeConfig = useMemo(() => getAntdTheme(darkModeEnabled), [darkModeEnabled])

    if (decided) return null

    return (
        <ConfigProvider theme={themeConfig}>
            <CookieConsentCard />
        </ConfigProvider>
    )
}
